import { Checkout } from "typings/shopify-storefront"

export const isString = (val: any) => {
  return typeof val === "string"
}

export const addSlash = (url: string | null | undefined): string =>
  url
    ? url?.endsWith("/") || url?.includes("?")
      ? url
        ? url
        : ""
      : `${url}/`
    : ""

export const addMultiSlash = (url: string | null | undefined): string =>
  url
    ? url?.includes("?")
      ? url?.replace(/^\/?([^/]+(?:\/[^/]+)*)\/?$/, "/$1")
      : url?.replace(/^\/?([^/]+(?:\/[^/]+)*)\/?$/, "/$1/")
    : ""

export const cleanUrlSlashes = (url: string | null | undefined): string =>
  url?.replace(/([^:]\/)\/+/g, "$1")

export const templateReplace = (original: string, replaced: string) =>
  original?.replace(/\{\{(.+?)\}\}/g, () => replaced)

export const createUrlFromTitle = (
  title: string,
  productPathPrefix: string
): string | null => {
  if (!title) {
    return null
  }

  return createProductUrl(slugify(title), productPathPrefix)
}

export const createProductUrl = (
  handle: string,
  productPathPrefix: string
): string | null => {
  if (!handle) {
    return null
  }
  return cleanUrlSlashes(`${productPathPrefix}${addMultiSlash(handle)}`)
}

export const createProductVariantUrl = (
  handle: string,
  variantId: string,
  productPathPrefix: string
): string | null => {
  if (!handle || !variantId) {
    return null
  }

  const baseUrl = createProductUrl(handle, productPathPrefix)

  return `${baseUrl}?variant=${variantId}`
}

export const slugify = (text: string) => {
  if (!text) return ""

  return text.trim().toLowerCase().replace(/\s/g, "-")
}

/**
 * decodes shopify gid that is base64 encoded
 * gid://shopify/Checkout/76be77557b5f99e5a00db38e5bfabed9?key=038819825913fd075d3222510e0bf95f
 */
export const decodeShopifyGid = (
  gid: string
): {
  type: string
  id: string
  params: Record<string, string>
} => {
  try {
    const raw = atob(gid).replace("gid://shopify/", "")
    const [type, id] = raw.split("/")
    const params = (raw.split("?").slice(1)[0] || "")
      .split("&")
      .reduce((p, q) => {
        const [key, value] = q.split("=")
        p[key] = value
        return p
      }, {} as Record<string, string>)
    return {
      type,
      id: id.split("?")[0],
      params,
    }
  } catch (e) {
    return {
      type: "",
      id: "",
      params: {},
    }
  }
}

export const isValidShopifyCheckoutGid = (gid: string): boolean => {
  const decoded = decodeShopifyGid(gid)
  return Boolean(decoded.id && decoded.params.key)
}

export const getCartFlyoutUrl = (checkout: Checkout): string => {
  const siteUrl = process.env.URL || "http://localhost:8000"
  const url = new URL(siteUrl)

  if (checkout) {
    url.searchParams.append("cart", checkout.id)
  }

  return url.toString()
}

export const getCartUrl = (checkout: Checkout): string => {
  const url = new URL(getCartFlyoutUrl(checkout))
  url.pathname = "/cart/"
  return url.toString()
}

export const snakeCase = (str: string) => str.toLowerCase().replace(/\s/g, "_")

export const isStringBase64 = (str: string) => {
  const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/
  return base64regex.test(str)
}

export const encodeShopifyId = (id: string) => {
  if (!isStringBase64(id)) {
    btoa(`gid://shopify/ProductVariant/${id}`)
  }
  return id
}

const imagesType = {
  "image/jpeg": true,
  "image/png": true,
}

export const isImage = (type?: string | null) => type && type in imagesType

export const decodeShopifyId = (encodedShopifyId?: string | null) => {
  if (encodedShopifyId) {
    if (!isStringBase64(encodedShopifyId)) {
      return encodedShopifyId
    }

    // atob is only available if running from the browser
    const decodedId =
      typeof atob !== "undefined"
        ? atob(encodedShopifyId)
        : Buffer.from(encodedShopifyId, "base64").toString()
    const lastSlashIndex = decodedId.lastIndexOf("/")

    if (lastSlashIndex > 0) {
      return decodedId.substr(lastSlashIndex + 1)
    }
  }

  return ""
}

export const capitalizeFirstLetter = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
